import { useEffect, useRef, useState, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { Typography } from '@mui/joy';
import { enqueueSnackbar } from 'notistack';
import { ErrorMessage, Field, FieldArray, Formik, FormikHelpers } from 'formik';
import RunTestFooter from './RunTestFooter';
import { MAX_BULK_TEST_LIMIT, runBulkTestSchema } from './RunBulkTestSchema';
import { PageType, Test } from './RunTest';
import RunTestRow from './RunTestRow';
import RunTestAddRow from './RunTestAddRow';
import ManualBulkImport from './ManualBulkImport/ManualBulkImport';
import ExcelBulkImport from './ExcelBulkImport/ExcelBulkImport';
import ImportedTestsPreview from './ImportedTestsPreview/ImportedTestsPreview';
import ImportedTestsValidationTable from './ImportedTestsValidationTable/ImportedTestsValidationTable';
import { ValidateImportedTests, ValidationError } from './ValidateImportedTests';
import { useRunBulkMutation } from '../../services/api.bulk';
import { RunTestsBody } from '../../interfaces/test.interface';
import { useAppSelector } from '../../services/store';
import { useAuth } from '../../hooks/useAuth';
import wait from '../../utils/wait';
import getErrorStatus from '../../utils/getErrorStatus';
import { EmailsBoxHandle } from '../EmailsBox/EmailsBox';

const importErrorMessage = `You've exceeded the limit on the number of tests you can take in single a batch. The maximum is ${MAX_BULK_TEST_LIMIT}.`;

interface RunTestBulkProps {
  onClose: () => void;
  setShowSuccessInfo: Dispatch<SetStateAction<boolean>>;
  setTestType: Dispatch<SetStateAction<string>>;
  handleCreate: (
    values: RunTestBulkInitialValues,
    setValues: Dispatch<SetStateAction<RunTestBulkInitialValues>>
  ) => void;
  handleDelete: (
    values: RunTestBulkInitialValues,
    setValues: Dispatch<SetStateAction<RunTestBulkInitialValues>>,
    index: number
  ) => void;
  pageTypes: PageType[];
  // runTestBulk DEMO
  hideTabs?: boolean;
}

export interface RunTestBulkInitialValues {
  bulk: string;
  tests: Test[];
  importedTests: Test[];
}

export interface ExcelBulkData {
  LINK: string;
  'TEST TYPE': string;
  FILTER: string;
}

const RunTestBulk = ({
  setShowSuccessInfo,
  onClose,
  handleDelete,
  handleCreate,
  pageTypes,
  setTestType,
  hideTabs
}: RunTestBulkProps) => {
  const { isSuperAdmin, group } = useAuth();
  const [runTestEmails, setRunTestEmails] = useState<string[]>([]);
  const emailsBoxRef = useRef<EmailsBoxHandle>(null);
  const [selectedPageType, setSelectedPageType] = useState<PageType | undefined>();
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [importSource, setImportSource] = useState<'manual' | 'excel'>('manual');
  const [manualImportData, setManualImportData] = useState<string>('');
  const [wrongUrls, setWrongUrls] = useState<{ url: string; message: string }[]>([]);
  const [manualImportError, setManualImportError] = useState<string>('');
  const [excelImportError, setExcelImportError] = useState<string>('');

  const WPC_PL_GROUP_ID = import.meta.env.VITE_WPC_PL_GROUP_ID;

  const userId = useAppSelector((store) => store.user.userId);
  const isImportErrors = wrongUrls.length > 0;

  const [runTest, { isSuccess }] = useRunBulkMutation();

  const initialValues = {
    bulk: '',
    tests: [],
    importedTests: []
  } as RunTestBulkInitialValues;

  const handleFixItemTestType = (testType: string) => {
    // remove space and lowercase
    return testType.replace(/\s/g, '').toLowerCase();
  };

  const handleUploadFilter = (label: string): string => {
    return label ? label.replace(/,/g, '&').replace(/\s/g, '').toLowerCase() : '';
  };

  const handleUpload = async (
    e: ChangeEvent<HTMLInputElement>,
    values: RunTestBulkInitialValues,
    setValues: Dispatch<SetStateAction<RunTestBulkInitialValues>>
  ) => {
    // exceljs is heavy and only needed once a file is picked, so load it here rather than with the nav bar.
    const { default: excelToJson } = await import('../../utils/excelToJson');
    const result = (await excelToJson(e)) as ExcelBulkData[] | undefined;
    if (!result) {
      return;
    }

    if (result.some((item: ExcelBulkData) => !item.LINK || !item['TEST TYPE'])) {
      setWrongUrls([{ url: '', message: 'Missing LINK or TEST TYPE in some row(s)' }]);
      return;
    }

    const totalUrlsAfterImport = values.importedTests.length + values.tests.length + result.length;
    if (totalUrlsAfterImport > MAX_BULK_TEST_LIMIT) {
      setExcelImportError(importErrorMessage);
      return;
    }

    setExcelImportError('');

    const importedTests = await Promise.all(
      result.map((item: ExcelBulkData) => ({
        name: item.LINK,
        pageType: handleFixItemTestType(item['TEST TYPE']),
        label: handleUploadFilter(item.FILTER)
      }))
    );

    const updatedTests = [...values.importedTests, ...importedTests].filter((item: Test) => item.name.trim() !== '');

    try {
      const validatedTests = await ValidateImportedTests(updatedTests, isSuperAdmin);
      setValues({
        ...values,
        importedTests: validatedTests
      });
      setWrongUrls([]);
    } catch (validationError) {
      if (Array.isArray(validationError)) {
        const wrongUrls = validationError.map((error: ValidationError) => ({
          url: error.value,
          message: error.message
        }));
        setWrongUrls(wrongUrls);
      }
    }
  };

  const handleSubmitForm = async (
    values: RunTestBulkInitialValues,
    { setSubmitting }: FormikHelpers<RunTestBulkInitialValues>
  ) => {
    const emails = emailsBoxRef.current?.flush();
    if (!emails) {
      setSubmitting(false);
      return;
    }

    const testUrls = [...values.importedTests, ...values.tests];

    const data: RunTestsBody = {
      bulk: true,
      bulkName: values.bulk,
      emails,
      links: testUrls.map((item: Test) => ({
        name: item.name.replace('https://', ''),
        pageType: item.pageType,
        label: item.label
      })),
      user: userId ? userId : undefined
    };

    try {
      await runTest({ data }).unwrap();
    } catch (err) {
      console.error(err);
      const status = getErrorStatus(err);
      if (status === 429) {
        enqueueSnackbar('You were blocked by test rate limiter', { variant: 'error' });
        await wait(2000);
        window.location.reload();
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const isWpcPlGroup = group && group.id === WPC_PL_GROUP_ID;

    const linkPageType = pageTypes?.find((pageType) =>
      isWpcPlGroup ? pageType.value === 'pre-qa' : pageType.value === 'brokenlink'
    );

    if (linkPageType) {
      setSelectedPageType(linkPageType);
    }
  }, [pageTypes, WPC_PL_GROUP_ID, group]);

  useEffect(() => {
    if (isSuccess) {
      setShowSuccessInfo(true);
      setTestType('bulkTestType');
    }
  }, [isSuccess, setShowSuccessInfo, setTestType]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmitForm}
        validationSchema={runBulkTestSchema(isSuperAdmin)}
      >
        {(props) => {
          const { values, handleChange, handleSubmit, setValues, isSubmitting } = props;

          const handleManualImport = async () => {
            const data = manualImportData.split(/[ ,;\r\n]/).filter((item) => item.trim() !== '');

            if (!data.length || !selectedPageType?.value) return;

            const totalUrlsAfterImport = values.importedTests.length + values.tests.length + data.length;
            if (totalUrlsAfterImport > MAX_BULK_TEST_LIMIT) {
              setManualImportError(importErrorMessage);
              return;
            }
            setManualImportError('');

            const importedTests = await Promise.all(
              data.map(async (item) => ({
                name: item,
                pageType: selectedPageType?.value || '',
                label: selectedFilter || ''
              }))
            );

            const updatedTests = [...values.importedTests, ...importedTests].filter(
              (item: Test) => item.name.trim() !== ''
            );

            try {
              const validatedTests = await ValidateImportedTests(updatedTests, isSuperAdmin);
              setValues({
                ...values,
                importedTests: validatedTests
              });
              setManualImportData('');
              setSelectedFilter('');
              setWrongUrls([]);
            } catch (validationError) {
              if (Array.isArray(validationError)) {
                const wrongUrls = validationError.map((error: ValidationError) => ({
                  url: error.value,
                  message: error.message
                }));
                setWrongUrls(wrongUrls);
              }
            }
          };

          const isBulkSizeExceeded = values.importedTests.length + values.tests.length > MAX_BULK_TEST_LIMIT;

          return (
            <form onSubmit={handleSubmit} className={'run-test-form'}>
              <div className="bulk-modal-content">
                <div className={'form-control'}>
                  <div className="input-container">
                    <label htmlFor={'bulk'}>Bulk name:</label>
                    <Field
                      id="bulk"
                      className="input-no-border"
                      value={values.bulk}
                      onChange={handleChange}
                      placeholder="e.g. WSC20202000-10000"
                    />
                  </div>
                  <ErrorMessage name={'bulk'} component={'p'} className={'form-control-error-message'} />
                </div>

                {!hideTabs ? (
                  <div className="bulk-importSourceBtns">
                    <button
                      className={`button importSourceBtn ${importSource === 'manual' && 'active'}`}
                      onClick={() => setImportSource('manual')}
                      type="button"
                    >
                      Manual
                    </button>
                    <button
                      className={`button importSourceBtn ${importSource === 'excel' && 'active'}`}
                      onClick={() => setImportSource('excel')}
                      type="button"
                    >
                      Excel
                    </button>
                  </div>
                ) : null}

                {importSource === 'manual' ? (
                  <ManualBulkImport
                    pageTypes={pageTypes}
                    manualImportData={manualImportData}
                    setManualImportData={setManualImportData}
                    selectedPageType={selectedPageType}
                    setSelectedPageType={setSelectedPageType}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    handleManualImport={handleManualImport}
                    disabled={isBulkSizeExceeded}
                    importError={manualImportError}
                  />
                ) : (
                  <ExcelBulkImport
                    handleUpload={handleUpload}
                    values={values}
                    setValues={setValues}
                    importError={excelImportError}
                  />
                )}
              </div>
              <div className="links-container">
                <ImportedTestsValidationTable wrongUrls={wrongUrls} />

                <ImportedTestsPreview
                  importedTests={values.importedTests}
                  setValues={setValues}
                  setWrongUrls={setWrongUrls}
                  pageTypes={pageTypes}
                />

                <ErrorMessage name={'importedTests'} component={'p'} className={'form-control-error-message'} />

                <FieldArray name="tests">
                  {() =>
                    values.tests.map((test, i) => (
                      <RunTestRow
                        key={i}
                        test={test}
                        index={i}
                        pageTypes={pageTypes}
                        values={values}
                        setValues={setValues}
                        handleChange={handleChange}
                        handleDelete={handleDelete}
                      />
                    ))
                  }
                </FieldArray>
              </div>

              <RunTestAddRow handleCreate={() => handleCreate(values, setValues)} disabled={isBulkSizeExceeded} />

              <RunTestFooter
                onClose={onClose}
                runTestEmails={runTestEmails}
                setRunTestEmails={setRunTestEmails}
                emailsBoxRef={emailsBoxRef}
                loading={isSubmitting}
                isImportErrors={isImportErrors}
                submitButtonDisabled={isBulkSizeExceeded}
              />
              {isBulkSizeExceeded && (
                <Typography level="body-md" color="danger" textAlign="center" paddingBottom={4}>
                  Maximum number of URLs in one bulk is {MAX_BULK_TEST_LIMIT}.
                </Typography>
              )}
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default RunTestBulk;
